import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import RefundModal from "@/components/RefundModal";
import PaymentPanel from "@/components/PaymentPanel";
import {
  ArrowLeft,
  User,
  Calendar,
  DollarSign,
  Ticket,
  FileText,
  RotateCcw,
  Lock,
  Plane,
  CreditCard,
  CheckCircle,
  XCircle,
  Send,
  AlertTriangle,
  Loader2,
  Pencil,
} from "lucide-react";
import { format } from "date-fns";
import CommunicationHistory from "@/components/CommunicationHistory";
import BookingAccountingPanel from "@/components/BookingAccountingPanel";
import BookingInvoicePanel from "@/components/BookingInvoicePanel";
import ReviewRequestButton from "@/components/ReviewRequestButton";
import ExtraServicesCard from "@/components/ExtraServicesCard";
import PageLoader from "../components/PageLoader";
import EmptyState from "../components/EmptyState";
import StatusBadge from "../components/StatusBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  const [booking, setBooking] = useState(null);
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRefund,       setShowRefund]       = useState(false);
  const [linkedInvoice,    setLinkedInvoice]    = useState(null);
  const [cancelledTickets, setCancelledTickets] = useState([]);

  // Amadeus-specific state
  const [sendingPayment, setSendingPayment]       = useState(false);
  const [sendingStripeTest, setSendingStripeTest] = useState(false);
  const [stripeTestMode, setStripeTestMode]       = useState(false);
  const [cancellingAmadeus, setCancellingAmadeus] = useState(false);
  const [showTicketModal, setShowTicketModal]     = useState(false);
  const [ticketNumbers, setTicketNumbers]         = useState([]);
  const [ticketNumbers2, setTicketNumbers2]       = useState([]);
  const [ticketIssuer, setTicketIssuer]           = useState("");
  const [savingTicket, setSavingTicket]           = useState(false);
  // Pay Later modal state
  const [showPayLaterModal, setShowPayLaterModal] = useState(false);
  const [payLaterDueDate, setPayLaterDueDate]     = useState("");
  const [payLaterNotes, setPayLaterNotes]         = useState("");
  const [sendingPayLater, setSendingPayLater]     = useState(false);
  // Base price inline edit state
  const [editingBase, setEditingBase]          = useState(false);
  const [baseInput, setBaseInput]              = useState("");
  const [savingBase, setSavingBase]            = useState(false);
  // Selling price inline edit state
  const [editingPrice, setEditingPrice]        = useState(false);
  const [priceInput, setPriceInput]            = useState("");
  const [savingPrice, setSavingPrice]          = useState(false);
  // Confirmed Itinerary state
  const [ciSending, setCiSending]             = useState(false);
  const [ciSendingTest, setCiSendingTest]     = useState(false);
  // PNR & ticket references inline edit state
  const [editingRefs, setEditingRefs]         = useState(false);
  const [refPnr, setRefPnr]                   = useState("");
  const [refPnr2, setRefPnr2]                 = useState("");
  const [refTickets, setRefTickets]           = useState([""]);
  const [refTickets2, setRefTickets2]         = useState([""]);
  const [savingRefs, setSavingRefs]           = useState(false);

  useEffect(() => {
    fetchBookingData();
    axios.get(`${API}/config`).then(r => setStripeTestMode(!!r.data.stripe_test_mode)).catch(() => {});
  }, [id]);

  const fetchBookingData = async () => {
    try {
      const bookingRes = await axios.get(`${API}/bookings/${id}`, { headers: getAuthHeaders() });
      setBooking(bookingRes.data);
      
      // Fetch associated request
      if (bookingRes.data.request_id) {
        const requestRes = await axios.get(`${API}/flight-requests/${bookingRes.data.request_id}`, { headers: getAuthHeaders() });
        setRequest(requestRes.data);
      }

      // Fetch cancelled tickets for partial-refund panel
      if (bookingRes.data.status === "partially_cancelled") {
        try {
          const ticketsRes = await axios.get(`${API}/tickets`, { params: { booking_id: bookingRes.data.id }, headers: getAuthHeaders() });
          const allTickets = ticketsRes.data?.tickets || ticketsRes.data || [];
          setCancelledTickets(allTickets.filter(t => t.status === "cancelled"));
        } catch (_) {
          setCancelledTickets([]);
        }
      } else {
        setCancelledTickets([]);
      }
    } catch (error) {
      toast.error("Failed to load booking data");
      navigate("/bookings");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${id}`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      toast.success("Status updated!");
      fetchBookingData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleSendAmadeusPayment = async () => {
    setSendingPayment(true);
    try {
      const res = await axios.post(`${API}/payments/create`, { booking_id: id }, { headers: getAuthHeaders() });
      toast.success(res.data.email_sent ? "Tikkie payment link sent to customer!" : "Tikkie payment link created");
      fetchBookingData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create Tikkie payment link");
    } finally { setSendingPayment(false); }
  };

  const handleSendStripeTest = async () => {
    setSendingStripeTest(true);
    try {
      const res = await axios.post(`${API}/payments/create-stripe-test`, { booking_id: id }, { headers: getAuthHeaders() });
      toast.success("Stripe test checkout created — opening in new tab");
      window.open(res.data.checkout_url, "_blank", "noopener,noreferrer");
      fetchBookingData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create Stripe test payment");
    } finally { setSendingStripeTest(false); }
  };

  const handleSendPayLater = async () => {
    setSendingPayLater(true);
    try {
      const res = await axios.post(
        `${API}/payments/pay-later`,
        { booking_id: id, due_date: payLaterDueDate, notes: payLaterNotes },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.email_sent ? "Bank transfer details sent to customer!" : "Pay Later set (no email sent)");
      setShowPayLaterModal(false);
      setPayLaterDueDate("");
      setPayLaterNotes("");
      fetchBookingData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to set Pay Later");
    } finally { setSendingPayLater(false); }
  };

  const openTicketModal = () => {
    const count = booking?.passengers?.length || 1;
    setTicketNumbers((booking?.ticket_numbers?.length ? booking.ticket_numbers : Array(count).fill("")));
    setTicketNumbers2((booking?.ticket_numbers_2?.length ? booking.ticket_numbers_2 : Array(count).fill("")));
    setTicketIssuer(booking?.ticket_issuer || "");
    setShowTicketModal(true);
  };

  const handleMarkTicketed = async () => {
    setSavingTicket(true);
    try {
      await axios.patch(`${API}/bookings/${id}`, {
        status: "ticketed",
        ticket_numbers: ticketNumbers.filter(Boolean),
        ticket_numbers_2: ticketNumbers2.filter(Boolean),
        ticket_issuer: ticketIssuer,
      }, { headers: getAuthHeaders() });
      toast.success("Booking marked as ticketed!");
      setShowTicketModal(false);
      fetchBookingData();
    } catch (e) {
      toast.error("Failed to mark as ticketed");
    } finally { setSavingTicket(false); }
  };

  const handleCancelAmadeus = async () => {
    if (!window.confirm("Cancel this Amadeus booking? This will cancel the reservation with Amadeus and cannot be undone.")) return;
    setCancellingAmadeus(true);
    try {
      await axios.delete(`${API}/flights/cancel-amadeus/${booking.amadeus_order_id}`, { headers: getAuthHeaders() });
      toast.success("Amadeus booking cancelled");
      fetchBookingData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel Amadeus booking");
    } finally { setCancellingAmadeus(false); }
  };

  const STATUS_LABELS = {
    confirmed:         "Confirmed",
    pending_ticketing: "Pending Ticketing",
    ticketed:          "Ticketed",
    completed:         "Completed",
    cancelled:         "Cancelled",
  };

  if (loading) {
    return (
      <DashboardLayout>
        <PageLoader message="Loading booking…" />
      </DashboardLayout>
    );
  }

  const openEditRefs = () => {
    const count = booking.passengers?.length || 1;
    setRefPnr(booking.pnr || "");
    setRefPnr2(booking.pnr2 || "");
    setRefTickets(booking.ticket_numbers?.length ? [...booking.ticket_numbers] : Array(count).fill(""));
    setRefTickets2(booking.ticket_numbers_2?.length ? [...booking.ticket_numbers_2] : Array(count).fill(""));
    setEditingRefs(true);
  };

  const saveBasePrice = async () => {
    const val = parseFloat(baseInput);
    if (!isFinite(val) || val <= 0) {
      toast.error("Base price must be a positive number");
      return;
    }
    setSavingBase(true);
    try {
      const newProfit = parseFloat((( booking.selling_price ?? 0) - val).toFixed(2));
      await axios.patch(`${API}/bookings/${id}`,
        { base_price: val, selling_price: booking.selling_price ?? 0, profit: newProfit },
        { headers: getAuthHeaders() }
      );
      toast.success("Base price updated");
      setEditingBase(false);
      fetchBookingData();
    } catch {
      toast.error("Failed to update base price");
    } finally {
      setSavingBase(false);
    }
  };

  const saveSellingPrice = async () => {
    const val = parseFloat(priceInput);
    if (!isFinite(val) || val <= 0) {
      toast.error("Selling price must be a positive number");
      return;
    }
    setSavingPrice(true);
    try {
      const newProfit = parseFloat((val - (booking.base_price ?? 0)).toFixed(2));
      await axios.patch(`${API}/bookings/${id}`,
        { selling_price: val, profit: newProfit },
        { headers: getAuthHeaders() }
      );
      toast.success("Selling price updated");
      setEditingPrice(false);
      fetchBookingData();
    } catch {
      toast.error("Failed to update selling price");
    } finally {
      setSavingPrice(false);
    }
  };

  const saveRefs = async () => {
    setSavingRefs(true);
    try {
      const payload = {
        ticket_numbers:   refTickets.filter(Boolean),
        ticket_numbers_2: refTickets2.filter(Boolean),
        pnr2:             refPnr2.trim() || null,
      };
      // Only include pnr if it has a value (avoids overwriting with undefined
      // on bookings that were created before the pnr field existed)
      const pnrVal = refPnr.trim();
      if (pnrVal) payload.pnr = pnrVal;
      await axios.patch(`${API}/bookings/${id}`, payload, { headers: getAuthHeaders() });
      toast.success("References saved");
      setEditingRefs(false);
      fetchBookingData();
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingRefs(false);
    }
  };

  const handleViewConfirmedItineraryPdf = async () => {
    try {
      const res = await axios.post(
        `${API}/bookings/${id}/confirmed-itinerary-pdf`,
        {},
        { headers: getAuthHeaders(), responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      toast.error("PDF generation failed");
    }
  };

  const handleSendConfirmedItinerary = async (isTest) => {
    const setter = isTest ? setCiSendingTest : setCiSending;
    setter(true);
    try {
      const res = await axios.post(
        `${API}/bookings/${id}/send-confirmed-itinerary`,
        { is_test: isTest },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message || "Email sent");
      fetchBookingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send email");
    } finally {
      setter(false);
    }
  };

  if (!booking) {
    return (
      <DashboardLayout>
        <EmptyState title="Booking not found" description="This booking could not be loaded." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="booking-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/bookings")} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="font-display text-3xl font-bold text-gray-900 font-mono">
                {booking.pnr}{booking.pnr2 && <span className="text-gray-400"> / {booking.pnr2}</span>}
              </h1>
              <StatusBadge status={booking.status} label={STATUS_LABELS[booking.status] || booking.status} />
            </div>
            <p className="text-gray-500 mt-1">Booking details and management</p>
          </div>
          {!booking.amadeus_order_id && (
            <Button
              onClick={() => navigate(`/tickets/${booking.id}`)}
              className="btn-accent"
              data-testid="generate-ticket-btn"
            >
              <Ticket className="w-4 h-4 mr-2" />
              {booking.ticket_email_sent ? "Update E-Ticket" : "Generate E-Ticket"}
            </Button>
          )}
          {!booking.is_refund && booking.status !== "partially_cancelled" && (
            <Button variant="outline" onClick={() => setShowRefund(true)}
              className="border-red-200 text-red-600 hover:bg-red-50">
              <RotateCcw className="w-4 h-4 mr-2" /> Create Refund
            </Button>
          )}
          {!booking.is_refund && booking.status === "partially_cancelled" && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm space-y-2">
              <p className="font-semibold text-amber-800">This booking has a partially cancelled ticket.</p>
              {linkedInvoice ? (
                <p className="text-amber-700 text-xs">
                  To record the refund, open the linked invoice and enter only the cancelled ticket's
                  amount in the cancellation panel.
                  <Button variant="link" size="sm" className="text-amber-800 underline h-auto p-0 ml-1"
                    onClick={() => navigate(`/invoices/${linkedInvoice.id}`)}>
                    Open Invoice →
                  </Button>
                </p>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-amber-700 text-xs flex-1">
                    Use the refund panel to record a refund for the cancelled ticket only.
                  </p>
                  <Button size="sm" variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50 shrink-0"
                    onClick={() => setShowRefund(true)}>
                    <RotateCcw className="w-3.5 h-3.5 mr-1" />Refund Ticket
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Pricing */}
            <Card data-testid="pricing-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Pricing Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Base Price</span>
                  {editingBase ? (
                    <div className="flex items-center gap-1">
                      <span className="text-slate-400 text-sm">€</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        className="w-24 text-right font-mono text-sm border border-slate-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-slate-400"
                        value={baseInput}
                        onChange={e => setBaseInput(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") saveBasePrice(); if (e.key === "Escape") setEditingBase(false); }}
                        autoFocus
                      />
                      <button onClick={saveBasePrice} disabled={savingBase}
                        className="text-xs text-emerald-600 hover:text-emerald-700 font-medium disabled:opacity-50 px-1">
                        {savingBase ? "…" : "Save"}
                      </button>
                      <button onClick={() => setEditingBase(false)}
                        className="text-xs text-slate-400 hover:text-slate-600 px-1">
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono font-medium">€{(booking.base_price ?? 0).toFixed(2)}</span>
                      {!booking.is_refund && (
                        <button
                          onClick={() => { setBaseInput((booking.base_price ?? 0).toFixed(2)); setEditingBase(true); }}
                          className="text-slate-400 hover:text-slate-600 p-0.5 rounded"
                          title="Edit base price">
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Selling Price</span>
                  {editingPrice ? (
                    <div className="flex items-center gap-1">
                      <span className="text-slate-400 text-sm">€</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        className="w-24 text-right font-mono text-sm border border-slate-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-slate-400"
                        value={priceInput}
                        onChange={e => setPriceInput(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") saveSellingPrice(); if (e.key === "Escape") setEditingPrice(false); }}
                        autoFocus
                      />
                      <button onClick={saveSellingPrice} disabled={savingPrice}
                        className="text-xs text-emerald-600 hover:text-emerald-700 font-medium disabled:opacity-50 px-1">
                        {savingPrice ? "…" : "Save"}
                      </button>
                      <button onClick={() => setEditingPrice(false)}
                        className="text-xs text-slate-400 hover:text-slate-600 px-1">
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono font-medium">€{(booking.selling_price ?? 0).toFixed(2)}</span>
                      {!booking.is_refund && (
                        <button
                          onClick={() => { setPriceInput((booking.selling_price ?? 0).toFixed(2)); setEditingPrice(true); }}
                          className="text-slate-400 hover:text-slate-600 p-0.5 rounded"
                          title="Edit selling price">
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {booking.is_refund ? (
                  <div className="border-t pt-4">
                    <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-center space-y-2">
                      <p className="text-xs font-semibold text-amber-700">Refund / Reversal Record</p>
                      <p className="text-xs text-amber-600 mt-0.5">Financial summary is on the original booking and its invoice.</p>
                      {booking.original_booking_id && (
                        <button
                          onClick={() => navigate(`/bookings/${booking.original_booking_id}`)}
                          className="inline-flex items-center gap-1 text-xs text-amber-800 underline underline-offset-2 hover:text-amber-900">
                          View Original Booking
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Profit</span>
                      <span className={`font-mono font-bold text-lg ${(booking.profit ?? 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        €{(booking.profit ?? 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Status Management */}
            <Card data-testid="status-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Status Management
                </CardTitle>
              </CardHeader>
              <CardContent>
              <Select value={booking.status} onValueChange={handleStatusChange}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="pending_ticketing">Pending Ticketing</SelectItem>
                    <SelectItem value="ticketed">Ticketed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="refunded">Refunded</SelectItem>
                  </SelectContent>
                </Select>

                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-slate-500 mb-2">Payment Status</p>
                  <div className="flex items-center justify-between">
                    <Badge className={
                      booking.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" :
                      booking.payment_status === "awaiting_payment" ? "bg-amber-100 text-amber-800" :
                      booking.payment_status === "pay_later" ? "bg-blue-100 text-blue-800" :
                      "bg-slate-100 text-slate-600"
                    }>
                      {booking.payment_status || "not set"}
                    </Badge>
                    {(() => {
                      const ps  = (booking.payment_status || "").toLowerCase();
                      const acc = booking.accounting || {};
                      const accPs    = (acc.payment_status || "").toLowerCase();
                      const received = parseFloat(acc.amount_received ?? booking.amount_received ?? 0) || 0;
                      const genuinelyPaid = ps === "paid" || accPs === "paid" || received >= (booking.selling_price || 0);
                      if (genuinelyPaid) return null;
                      return (
                        <Button size="sm" variant="outline"
                          className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 text-xs"
                          onClick={async () => {
                            try {
                              const today = new Date().toISOString().split("T")[0];
                              await Promise.all([
                                axios.patch(`${API}/bookings/${id}`,
                                  { status: "paid", payment_status: "paid" },
                                  { headers: getAuthHeaders() }
                                ),
                                axios.patch(`${API}/bookings/${id}/accounting`,
                                  { payment_status: "Paid", date_received: today, amount_received: booking.selling_price },
                                  { headers: getAuthHeaders() }
                                ),
                              ]);
                              toast.success("Marked as paid");
                              fetchBookingData();
                            } catch {
                              toast.error("Failed to update");
                            }
                          }}>
                          ✓ Mark as Paid
                        </Button>
                      );
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Accounting — locked until e-ticket has been emailed */}
            {booking.ticket_email_sent ? (
              <BookingAccountingPanel booking={booking} />
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-slate-400">
                    <Lock className="w-5 h-5" />
                    Accounting / Bookkeeping
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-4">
                    <Lock className="w-8 h-8 mx-auto mb-3 text-slate-200" />
                    <p className="text-sm font-medium text-slate-500">Locked</p>
                    <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                      Send the e-ticket to the customer first.<br />
                      Bookkeeping unlocks automatically after sending.
                    </p>
                    <Button
                      size="sm"
                      className="mt-4 btn-accent"
                      onClick={() => navigate(`/tickets/${booking.id}`)}
                    >
                      <Ticket className="w-3 h-3 mr-1.5" />
                      Go to E-Ticket
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <PaymentPanel booking={booking} onUpdate={fetchBookingData} />

            <BookingInvoicePanel booking={booking} onUpdate={fetchBookingData} onInvoiceFound={setLinkedInvoice} />

            {/* Booking Info */}
            <Card data-testid="booking-info-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Booking Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Offer Type</span>
                  <Badge variant="outline" className="capitalize">{booking.offer_type}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Created</span>
                  <span className="text-sm">{format(new Date(booking.created_at), "MMM d, yyyy HH:mm")}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Currency</span>
                  <span>{booking.currency}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Amadeus Panel + Extra Services + Travel Details */}
          <div className="lg:col-span-2 space-y-6">

            {/* ══ AMADEUS BOOKING PANEL ══ */}
            {booking.amadeus_order_id && (() => {
              const isPaid             = booking.payment_status === "paid";
              const isPendingTicketing = booking.status === "pending_ticketing";
              const isTicketed         = ["ticketed","completed"].includes(booking.status);
              const isETicketed        = !!booking.ticket_email_sent;
              const isCancelled        = booking.status === "cancelled";
              const paymentSent        = !!booking.payment_url || !!booking.stripe_session_id ||
                ["awaiting_payment", "paid", "pay_later"].includes(booking.payment_status);

              const steps = [
                { label: "Booked",            done: true },
                { label: "Payment Sent",      done: paymentSent },
                { label: "Paid",              done: isPaid },
                { label: "Pending Ticketing", done: isPaid && (isPendingTicketing || isTicketed) },
                { label: "Ticketed",          done: isTicketed },
                { label: "E-Ticket Sent",     done: isETicketed },
              ];

              return (
                <Card className="border-purple-200 bg-purple-50/30">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2 text-purple-800">
                      <Plane className="w-5 h-5" /> Amadeus Booking
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* PNR + order */}
                    <div className="flex items-center gap-6 flex-wrap">
                      <div>
                        <p className="text-xs text-purple-500 uppercase tracking-wide">PNR</p>
                        <p className="font-mono font-bold text-2xl text-purple-900">{booking.pnr}</p>
                      </div>
                      <div>
                        <p className="text-xs text-purple-500 uppercase tracking-wide">Amadeus Order</p>
                        <p className="font-mono text-sm text-purple-700">{booking.amadeus_order_id}</p>
                      </div>
                      <div className="ml-auto">
                        <Badge className={isCancelled ? "bg-red-100 text-red-800" : isTicketed ? "bg-emerald-100 text-emerald-800" : isPendingTicketing ? "bg-amber-100 text-amber-800" : "bg-purple-100 text-purple-800"}>
                          {booking.status === "pending_ticketing" ? "Pending Ticketing" : booking.status}
                        </Badge>
                      </div>
                    </div>

                    {/* Status timeline */}
                    <div className="flex items-center gap-0 overflow-x-auto">
                      {steps.map((step, i) => (
                        <div key={i} className="flex items-center">
                          <div className="flex flex-col items-center">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step.done ? "bg-emerald-500 text-white" : "bg-gray-200 text-gray-500"}`}>
                              {step.done ? "✓" : i + 1}
                            </div>
                            <span className={`text-[10px] mt-1 whitespace-nowrap ${step.done ? "text-emerald-600 font-medium" : "text-gray-400"}`}>{step.label}</span>
                          </div>
                          {i < steps.length - 1 && <div className={`w-8 h-0.5 mx-1 mb-4 ${steps[i + 1].done ? "bg-emerald-400" : "bg-gray-200"}`} />}
                        </div>
                      ))}
                    </div>

                    {!isCancelled && (
                      <div className="border-t border-purple-200 pt-3">
                        <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-3">Next Steps</p>
                        <div className="grid grid-cols-2 gap-2">
                          {/* Step 1: Payment options */}
                          <div className={`col-span-2 ${isPaid ? "opacity-60 pointer-events-none" : ""}`}>
                            <p className="text-[11px] text-purple-500 uppercase tracking-wide font-semibold mb-1.5">
                              1. Send Payment
                              {isPaid && <span className="ml-2 text-emerald-600 normal-case font-medium">✓ Received</span>}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {/* Tikkie */}
                              <Button
                                onClick={handleSendAmadeusPayment}
                                disabled={sendingPayment || isPaid}
                                variant="outline"
                                className="border-purple-300 text-purple-700 hover:bg-purple-50 h-9 px-3">
                                {sendingPayment ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <span className="mr-1.5">📱</span>}
                                Tikkie
                              </Button>
                              {/* Pay Later */}
                              <Button
                                onClick={() => setShowPayLaterModal(true)}
                                disabled={isPaid}
                                variant="outline"
                                className="border-blue-300 text-blue-700 hover:bg-blue-50 h-9 px-3">
                                <span className="mr-1.5">🏦</span>Pay Later
                              </Button>
                              {/* Stripe Test — only when STRIPE_TEST_MODE=true */}
                              {stripeTestMode && (
                                <Button
                                  onClick={handleSendStripeTest}
                                  disabled={sendingStripeTest || isPaid}
                                  variant="outline"
                                  className="border-yellow-400 text-yellow-700 hover:bg-yellow-50 h-9 px-3">
                                  {sendingStripeTest ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <span className="mr-1.5">💳</span>}
                                  Stripe Test
                                  <span className="ml-1.5 bg-yellow-200 text-yellow-800 text-[9px] font-bold px-1 py-0.5 rounded uppercase tracking-wide">TEST</span>
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Step 2: Ticketing */}
                          {isTicketed ? (
                            <div className="flex items-center gap-1.5 text-emerald-600 text-sm py-1 col-span-1">
                              <CheckCircle className="w-4 h-4" /> Ticketed ✓
                            </div>
                          ) : isPendingTicketing ? (
                            <Button
                              onClick={openTicketModal}
                              variant="outline"
                              className="border-amber-300 text-amber-700 hover:bg-amber-50 justify-start">
                              <CheckCircle className="w-4 h-4 mr-2" />
                              2. Enter Ticket Numbers
                            </Button>
                          ) : (
                            <Button
                              onClick={openTicketModal}
                              disabled={!isPaid}
                              variant="outline"
                              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 justify-start">
                              <CheckCircle className="w-4 h-4 mr-2" />
                              2. Mark as Ticketed
                            </Button>
                          )}

                          {/* Step 3: E-Ticket — enabled only after ticketed */}
                          <Button
                            onClick={() => navigate(`/tickets/${booking.id}`)}
                            disabled={!isTicketed}
                            className={`bg-purple-600 hover:bg-purple-700 text-white justify-start ${!isTicketed ? "opacity-50" : ""}`}>
                            <Send className="w-4 h-4 mr-2" />
                            {isETicketed ? "3. Update E-Ticket" : "3. Send E-Ticket"}
                          </Button>

                          {/* Step 4: Cancel */}
                          <Button
                            onClick={handleCancelAmadeus}
                            disabled={cancellingAmadeus || isCancelled}
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50 justify-start">
                            {cancellingAmadeus ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                            4. Cancel Booking
                          </Button>
                        </div>
                      </div>
                    )}

                    {isCancelled && (
                      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                        <AlertTriangle className="w-4 h-4 shrink-0" /> This booking has been cancelled with Amadeus.
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* ══ EXTRA SERVICES ══ */}
            <ExtraServicesCard booking={booking} onUpdate={fetchBookingData} />

            {/* ══ CONFIRMED ITINERARY — TICKET PENDING ══ */}
            {!["cancelled"].includes(booking.status) && (() => {
              const isCancelled   = booking.status === "cancelled";
              const isRefunded    = booking.status === "refunded";
              const isPaid        = booking.payment_status === "paid";
              const hasTickets    = (booking.ticket_numbers?.filter(Boolean)?.length > 0) ||
                                    (booking.ticket_numbers_2?.filter(Boolean)?.length > 0);
              // Payment status is NOT a blocker — only cancelled, refunded, and ticketed block actions
              const ciEligible    = !isCancelled && !isRefunded && !hasTickets;

              let ciBlockedReason = null;
              if (isRefunded)      ciBlockedReason = "Booking is refunded.";
              else if (hasTickets) ciBlockedReason = "Tickets have already been issued — send the e-ticket instead.";

              const sentAt   = booking.confirmed_itinerary_sent_at;
              const sentTo   = booking.confirmed_itinerary_sent_to;
              const leadEmail = booking.passengers?.[0]?.email || "";
              return (
                <Card className={`border-blue-200 ${ciEligible ? "bg-blue-50/30" : "bg-slate-50/50 opacity-75"}`} data-testid="confirmed-itinerary-card">
                  <CardHeader className="pb-3">
                    <CardTitle className={`text-lg flex items-center gap-2 ${ciEligible ? "text-blue-800" : "text-slate-400"}`}>
                      <FileText className="w-5 h-5" />
                      Confirmed Itinerary — Ticket Pending
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {ciBlockedReason ? (
                      <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        {ciBlockedReason}
                      </div>
                    ) : (
                      <p className="text-xs text-blue-600 leading-relaxed">
                        Send the confirmed flight itinerary with a notice that tickets are being processed.
                      </p>
                    )}

                    {ciEligible && !isPaid && (
                      <div className="flex items-start gap-2 p-2.5 bg-orange-50 border border-orange-300 rounded text-xs text-orange-800">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>
                          Payment has not yet been received. Please verify payment before proceeding.
                          The PDF and email will include a payment-pending notice for the customer.
                        </span>
                      </div>
                    )}

                    {sentAt && (
                      <div className="flex items-start gap-2 p-2.5 bg-blue-100 border border-blue-200 rounded text-xs text-blue-700">
                        <CheckCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>
                          Last sent to <strong>{sentTo}</strong><br />
                          <span className="text-blue-500">{new Date(sentAt).toLocaleString()}</span>
                        </span>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleViewConfirmedItineraryPdf}
                        className="border-blue-300 text-blue-700 hover:bg-blue-50"
                        disabled={!ciEligible}
                      >
                        <FileText className="w-3.5 h-3.5 mr-1.5" />
                        View PDF
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSendConfirmedItinerary(true)}
                        disabled={ciSendingTest || !ciEligible}
                        className="border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                      >
                        {ciSendingTest
                          ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          : <Send className="w-3.5 h-3.5 mr-1.5" />}
                        Test Email
                        <span className="ml-1.5 bg-yellow-200 text-yellow-800 text-[9px] font-bold px-1 py-0.5 rounded uppercase tracking-wide">TEST</span>
                      </Button>

                      <Button
                        size="sm"
                        onClick={() => handleSendConfirmedItinerary(false)}
                        disabled={ciSending || !ciEligible || !leadEmail}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {ciSending
                          ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          : <Send className="w-3.5 h-3.5 mr-1.5" />}
                        {sentAt ? "Resend to Customer" : "Send to Customer"}
                      </Button>
                    </div>

                    {ciEligible && !leadEmail && (
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        No email address on lead passenger — cannot send to customer.
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* ══ PNR & TICKET NUMBERS ══ always visible, always editable ══ */}
            <Card data-testid="booking-refs-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2"><Ticket className="w-5 h-5" /> PNR &amp; Ticket Numbers</span>
                  {!editingRefs && (
                    <Button size="sm" variant="outline" onClick={openEditRefs} className="text-xs">Edit</Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {editingRefs ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">PNR 1 (primary)</Label>
                        <Input value={refPnr} onChange={e => setRefPnr(e.target.value)} className="font-mono mt-1" placeholder="e.g. ABC123" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">PNR 2 (optional)</Label>
                        <Input value={refPnr2} onChange={e => setRefPnr2(e.target.value)} className="font-mono mt-1" placeholder="e.g. XY5678" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Ticket Numbers — PNR 1 (one per passenger)</Label>
                      {refTickets.map((t, i) => (
                        <div key={i} className="flex gap-2 mb-1.5">
                          <Input value={t} onChange={e => { const a = [...refTickets]; a[i] = e.target.value; setRefTickets(a); }}
                            className="font-mono text-xs" placeholder={`Pax ${i + 1} ticket`} />
                          {refTickets.length > 1 && (
                            <Button size="sm" variant="ghost" className="px-2 text-slate-400 hover:text-red-500"
                              onClick={() => setRefTickets(refTickets.filter((_, j) => j !== i))}>✕</Button>
                          )}
                        </div>
                      ))}
                      <Button size="sm" variant="ghost" className="text-xs text-slate-400 mt-0.5"
                        onClick={() => setRefTickets([...refTickets, ""])}>+ Add row</Button>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Ticket Numbers — PNR 2 (optional)</Label>
                      {refTickets2.map((t, i) => (
                        <div key={i} className="flex gap-2 mb-1.5">
                          <Input value={t} onChange={e => { const a = [...refTickets2]; a[i] = e.target.value; setRefTickets2(a); }}
                            className="font-mono text-xs" placeholder={`Pax ${i + 1} ticket`} />
                          {refTickets2.length > 1 && (
                            <Button size="sm" variant="ghost" className="px-2 text-slate-400 hover:text-red-500"
                              onClick={() => setRefTickets2(refTickets2.filter((_, j) => j !== i))}>✕</Button>
                          )}
                        </div>
                      ))}
                      <Button size="sm" variant="ghost" className="text-xs text-slate-400 mt-0.5"
                        onClick={() => setRefTickets2([...refTickets2, ""])}>+ Add row</Button>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" className="bg-slate-900 hover:bg-slate-700 text-white" onClick={saveRefs} disabled={savingRefs}>
                        {savingRefs ? "Saving…" : "Save"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingRefs(false)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">PNR 1</p>
                        <p className="font-mono font-bold text-slate-900">{booking.pnr || "—"}</p>
                        {(booking.ticket_numbers || []).filter(Boolean).length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {booking.ticket_numbers.filter(Boolean).map((t, i) => (
                              <p key={i} className="font-mono text-xs text-slate-600">✈ {t}</p>
                            ))}
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">PNR 2</p>
                        <p className="font-mono font-bold text-slate-900">{booking.pnr2 || <span className="text-slate-300 font-normal">not set</span>}</p>
                        {(booking.ticket_numbers_2 || []).filter(Boolean).length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {booking.ticket_numbers_2.filter(Boolean).map((t, i) => (
                              <p key={i} className="font-mono text-xs text-slate-600">✈ {t}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {booking.ticket_issuer && (
                      <p className="text-xs text-slate-400">Issued by: <span className="text-slate-600">{booking.ticket_issuer}</span></p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Passengers */}
            <Card data-testid="passengers-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Passengers ({booking.passengers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {booking.passengers.map((passenger, index) => (
                    <div key={index} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">
                          {passenger.title} {passenger.first_name} {passenger.last_name}
                        </span>
                        <Badge variant="outline" className="capitalize">{(() => { const g = (passenger.gender||"").toLowerCase(); const t = passenger.title||""; if (["f","female"].includes(g) || ["mrs","ms","miss"].includes(t.toLowerCase())) return "Female"; if (["m","male"].includes(g) || ["mr","mstr","dr"].includes(t.toLowerCase())) return "Male"; return passenger.gender || "—"; })()}</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm text-slate-600">
                        <div>
                          <span className="text-slate-400">Email: </span>
                          {passenger.email}
                        </div>
                        <div>
                          <span className="text-slate-400">Phone: </span>
                          {passenger.phone}
                        </div>
                        <div>
                          <span className="text-slate-400">DOB: </span>
                          {passenger.date_of_birth}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Trip Details from Request */}
            {request && (
              <Card data-testid="trip-card">
                <CardHeader>
                  <CardTitle className="text-lg">Trip Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-slate-400">Travel Type</span>
                        <p className="font-medium capitalize">{request.travel_type.replace("_", " ")}</p>
                      </div>
                      <div>
                        <span className="text-slate-400">Class</span>
                        <p className="font-medium capitalize">{request.travel_class}</p>
                      </div>
                      <div>
                        <span className="text-slate-400">Baggage</span>
                        <p className="font-medium capitalize">{request.baggage.replace("_", " ")}</p>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <p className="text-sm text-slate-400 mb-3">Flight Segments</p>
                      {request.segments.map((segment, index) => (
                        <div key={index} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg mb-2">
                          <span className="font-mono font-bold">{segment.departure_airport}</span>
                          <span className="text-slate-400">→</span>
                          <span className="font-mono font-bold">{segment.arrival_airport}</span>
                          <span className="ml-auto text-sm text-slate-500">{segment.departure_date}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ══ REVIEW REQUEST ══ */}
            <Card>
              <CardContent className="p-4">
                <ReviewRequestButton
                  sourceType="booking"
                  sourceId={booking.id}
                  customerName={`${booking.passengers?.[0]?.first_name || ""} ${booking.passengers?.[0]?.last_name || ""}`.trim()}
                  customerEmail={booking.passengers?.[0]?.email || ""}
                />
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
      {showRefund && (
        <RefundModal
          booking={booking}
          linkedInvoice={linkedInvoice}
          cancelledTickets={cancelledTickets}
          onClose={() => setShowRefund(false)}
          onSuccess={fetchBookingData}
        />
      )}

      {/* Communication history */}
      {booking?.id && (
        <div className="max-w-5xl mx-auto px-4 pb-4">
          <CommunicationHistory
            sourceType="booking"
            sourceId={booking.id}
            bookingId={booking.id}
          />
        </div>
      )}

      {/* Mark as Ticketed modal */}
      <Dialog open={showTicketModal} onOpenChange={setShowTicketModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-600" />Mark as Ticketed</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {/* PNR 1 ticket numbers */}
            <div>
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">PNR 1 — {booking?.pnr}</p>
              {booking?.passengers?.map((pax, i) => (
                <div key={i} className="space-y-1 mb-2">
                  <Label className="text-xs">{pax.title} {pax.first_name} {pax.last_name}</Label>
                  <Input
                    value={ticketNumbers[i] || ""}
                    onChange={e => { const t = [...ticketNumbers]; t[i] = e.target.value; setTicketNumbers(t); }}
                    placeholder="e.g. 157-1234567890"
                    className="font-mono"
                  />
                </div>
              ))}
            </div>
            {/* PNR 2 ticket numbers */}
            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                PNR 2 (optional){booking?.pnr2 && ` — ${booking.pnr2}`}
              </p>
              <p className="text-xs text-slate-400 mb-2">Leave blank if this booking only has one PNR.</p>
              {booking?.passengers?.map((pax, i) => (
                <div key={i} className="space-y-1 mb-2">
                  <Label className="text-xs">{pax.title} {pax.first_name} {pax.last_name}</Label>
                  <Input
                    value={ticketNumbers2[i] || ""}
                    onChange={e => { const t = [...ticketNumbers2]; t[i] = e.target.value; setTicketNumbers2(t); }}
                    placeholder="e.g. 074-9876543210"
                    className="font-mono"
                  />
                </div>
              ))}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Issuing Airline / Consolidator</Label>
              <Input value={ticketIssuer} onChange={e => setTicketIssuer(e.target.value)} placeholder="e.g. Pegasus Airlines" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowTicketModal(false)}>Cancel</Button>
            <Button onClick={handleMarkTicketed} disabled={savingTicket} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {savingTicket ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : "Confirm Ticketed"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Later Modal */}
      <Dialog open={showPayLaterModal} onOpenChange={setShowPayLaterModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              🏦 Send Bank Transfer Details
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-500">
              Sends the booking total and our IBAN to the customer by email so they can pay by bank transfer.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="pay-later-due">Payment due by (optional)</Label>
              <Input
                id="pay-later-due"
                type="date"
                value={payLaterDueDate}
                onChange={e => setPayLaterDueDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pay-later-notes">Notes for customer (optional)</Label>
              <Input
                id="pay-later-notes"
                placeholder="e.g. Please use booking reference as payment description"
                value={payLaterNotes}
                onChange={e => setPayLaterNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowPayLaterModal(false)}>Cancel</Button>
            <Button
              onClick={handleSendPayLater}
              disabled={sendingPayLater}
              className="bg-blue-600 hover:bg-blue-700 text-white">
              {sendingPayLater ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : "Send Bank Details"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
